import type { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import type { AuthService } from '../../../core/services/auth.service';
import type { SnackbarService } from '../../../core/services/snackbar.service';
import type { IUser } from '../../../shared/models/user.model';

/**
 * Error messages used across resolvers
 */
const RESOLVER_ERROR_MESSAGES = {
  USER_NOT_AUTHENTICATED: 'User not authenticated',
  MARKET_CONTEXT_NOT_AVAILABLE: 'Market context not available',
  MANAGER_NOT_FOUND: 'Manager not found',
  FAILED_TO_LOAD_DATA: 'Failed to load required data',
  INVALID_MANAGER_DETAILS: 'Invalid manager details',
  INVALID_CYCLE_ID: 'Invalid cycle ID',
  INVALID_CYCLE_OR_MANAGER: 'Invalid cycle or manager details'
} as const;

/**
 * Redirect paths used across resolvers
 */
const REDIRECT_PATHS = {
  DASHBOARD: '/dashboard',
  LOGIN: '/login',
  ACCESS_DENIED: '/access-denied'
} as const;

/**
 * Validates user authentication and returns current user
 *
 * @param authService - Authentication service instance
 * @param snackbarService - Snackbar notification service
 * @param router - Angular router for navigation
 * @returns Current authenticated user or null if not authenticated
 */
export function validateAuthentication(
  authService: AuthService,
  snackbarService: SnackbarService,
  router: Router
): IUser | null {
  const currentUser = authService.userDataValue;

  if (!currentUser) {
    snackbarService.showError(RESOLVER_ERROR_MESSAGES.USER_NOT_AUTHENTICATED);
    router.navigate([REDIRECT_PATHS.LOGIN]);
    return null;
  }

  return currentUser;
}

/**
 * Extracts market ID from user's selected role context or first available role
 *
 * @param authService - Authentication service instance
 * @param currentUser - Currently authenticated user
 * @param snackbarService - Snackbar notification service
 * @param router - Angular router for navigation
 * @returns Market ID or null if not available
 */
export function getMarketIdFromContext(
  authService: AuthService,
  currentUser: IUser,
  snackbarService: SnackbarService,
  router: Router
): number | null {
  const selectedRoleContext = authService.getSelectedRoleContext();

  const marketId = selectedRoleContext?.market_id ??
                   currentUser.system_role_info?.[0]?.market_id;

  if (!marketId) {
    snackbarService.showError(RESOLVER_ERROR_MESSAGES.MARKET_CONTEXT_NOT_AVAILABLE);
    router.navigate([REDIRECT_PATHS.DASHBOARD]);
    return null;
  }

  return marketId;
}

/**
 * Handles resolver errors consistently with logging, user notification, and navigation
 *
 * @param error - Error object from failed resolver operation
 * @param errorMessage - Descriptive error message for logging
 * @param snackbarService - Snackbar notification service
 * @param router - Angular router for navigation
 * @param redirectPath - Path to redirect user after error (default: '/dashboard')
 * @returns Observable of null to complete the resolver stream
 */
export function handleResolverError(
  error: unknown,
  errorMessage: string,
  snackbarService: SnackbarService,
  router: Router,
  redirectPath: string = REDIRECT_PATHS.DASHBOARD
): Observable<null> {
  console.error(errorMessage, error);
  snackbarService.showError(RESOLVER_ERROR_MESSAGES.FAILED_TO_LOAD_DATA);
  router.navigate([redirectPath]);
  return of(null);
}

/**
 * Validates that manager user data exists and has required properties
 *
 * @param manager - Manager user object to validate
 * @param snackbarService - Snackbar notification service
 * @param router - Angular router for navigation
 * @returns True if manager data is valid, false otherwise
 */
export function validateManagerData(
  manager: IUser | null,
  snackbarService: SnackbarService,
  router: Router
): boolean {
  if (!manager?.id) {
    snackbarService.showError(RESOLVER_ERROR_MESSAGES.MANAGER_NOT_FOUND);
    router.navigate([REDIRECT_PATHS.DASHBOARD]);
    return false;
  }
  return true;
}

/**
 * Validates route query parameter exists
 *
 * @param paramValue - Query parameter value to validate
 * @param errorMessage - Error message to display if validation fails
 * @param snackbarService - Snackbar notification service
 * @param router - Angular router for navigation
 * @returns True if parameter is valid (type guard), false otherwise
 */
export function validateQueryParam(
  paramValue: string | null,
  errorMessage: string,
  snackbarService: SnackbarService,
  router: Router
): paramValue is string {
  if (!paramValue) {
    snackbarService.showError(errorMessage);
    router.navigate([REDIRECT_PATHS.DASHBOARD]);
    return false;
  }
  return true;
}

/**
 * Validates that cycle ID is a positive number
 *
 * @param cycleIdParam - Cycle ID string from route parameter
 * @param snackbarService - Snackbar notification service
 * @param router - Angular router for navigation
 * @returns Parsed cycle ID or null if invalid
 */
export function validateCycleId(
  cycleIdParam: string | null,
  snackbarService: SnackbarService,
  router: Router
): number | null {
  if (!cycleIdParam) {
    snackbarService.showError(RESOLVER_ERROR_MESSAGES.INVALID_CYCLE_OR_MANAGER);
    router.navigate([REDIRECT_PATHS.DASHBOARD]);
    return null;
  }

  const cycleId = Number(cycleIdParam);
  if (isNaN(cycleId) || cycleId <= 0) {
    snackbarService.showError(RESOLVER_ERROR_MESSAGES.INVALID_CYCLE_ID);
    router.navigate([REDIRECT_PATHS.DASHBOARD]);
    return null;
  }

  return cycleId;
}

/**
 * Export constants for use in other files
 */
export { RESOLVER_ERROR_MESSAGES, REDIRECT_PATHS };
